import demoTrackPhotoUrl from '@/assets/demo/esteira.jpg'
import demoTrackThumbnailUrl from '@/assets/demo/esteira-thumb.jpg'
import demoHydraulicPhotoUrl from '@/assets/demo/hidraulica.jpg'
import demoHydraulicThumbnailUrl from '@/assets/demo/hidraulica-thumb.jpg'
import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { writeAuditLog } from '@/db/repositories/helpers'
import { ClientRepository } from '@/db/repositories/clientRepository'
import { InspectionRepository } from '@/db/repositories/inspectionRepository'
import { MachineRepository } from '@/db/repositories/machineRepository'
import { PhotoRepository } from '@/db/repositories/photoRepository'
import { TechnicianRepository } from '@/db/repositories/technicianRepository'
import { summarizeInspectionResults } from '@/db/rules'
import { invalidateBackupStatus } from '@/db/services/backupStatusService'
import type { Client, Inspection, Machine, Photo, Technician } from '@/db/types'

const DEMO_BATCH_ID = 'official-demo-v2'

interface DemoPhotoAsset {
  full: Blob
  thumbnail: Blob
  width: number
  height: number
  thumbnailWidth: number
  thumbnailHeight: number
}

export interface DemoAssetCollection {
  hydraulic: DemoPhotoAsset
  track: DemoPhotoAsset
}

export type DemoAssetLoader = () => Promise<DemoAssetCollection>

export interface DemoDataResult {
  client: Client
  machine: Machine
  technician: Technician
  inspection: Inspection
  photos: Photo[]
}

interface DemoReferences {
  clientId: string
  machineId: string
  technicianId: string
  inspectionId?: string
  photoIds: string[]
}

async function fetchJpeg(url: string): Promise<Blob> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new DatabaseOperationError(
      'unavailable',
      `Asset de demonstração indisponível: ${url}.`,
      'Não foi possível carregar as imagens locais de demonstração.',
    )
  }
  return new Blob([await response.arrayBuffer()], { type: 'image/jpeg' })
}

async function loadBundledDemoAssets(): Promise<DemoAssetCollection> {
  const [hydraulicFull, hydraulicThumbnail, trackFull, trackThumbnail] = await Promise.all([
    fetchJpeg(demoHydraulicPhotoUrl),
    fetchJpeg(demoHydraulicThumbnailUrl),
    fetchJpeg(demoTrackPhotoUrl),
    fetchJpeg(demoTrackThumbnailUrl),
  ])
  return {
    hydraulic: {
      full: hydraulicFull,
      thumbnail: hydraulicThumbnail,
      width: 1280,
      height: 853,
      thumbnailWidth: 320,
      thumbnailHeight: 213,
    },
    track: {
      full: trackFull,
      thumbnail: trackThumbnail,
      width: 1280,
      height: 853,
      thumbnailWidth: 320,
      thumbnailHeight: 213,
    },
  }
}

function readReferences(value: Record<string, unknown>): DemoReferences {
  const clientId = value.clientId
  const machineId = value.machineId
  const technicianId = value.technicianId
  const inspectionId = value.inspectionId
  const photoIds = value.photoIds
  if (
    typeof clientId !== 'string' ||
    typeof machineId !== 'string' ||
    typeof technicianId !== 'string' ||
    (inspectionId !== undefined && typeof inspectionId !== 'string') ||
    (photoIds !== undefined &&
      (!Array.isArray(photoIds) || photoIds.some((id) => typeof id !== 'string')))
  ) {
    throw new DatabaseOperationError('validation', 'Marcador de demonstração inválido.')
  }
  return {
    clientId,
    machineId,
    technicianId,
    ...(typeof inspectionId === 'string' ? { inspectionId } : {}),
    photoIds: Array.isArray(photoIds) ? (photoIds as string[]) : [],
  }
}

function currentCivilDate() {
  const now = new Date()
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}

function assertAsset(asset: DemoPhotoAsset, label: string) {
  if (
    asset.full.type !== 'image/jpeg' ||
    asset.thumbnail.type !== 'image/jpeg' ||
    asset.full.size === 0 ||
    asset.thumbnail.size === 0
  ) {
    throw new DatabaseOperationError(
      'validation',
      `Asset de demonstração inválido: ${label}.`,
      'As imagens locais de demonstração estão inválidas.',
    )
  }
}

export class DemoDataService {
  private readonly clients: ClientRepository
  private readonly machines: MachineRepository
  private readonly technicians: TechnicianRepository
  private readonly inspections: InspectionRepository
  private readonly photos: PhotoRepository

  constructor(
    private readonly database: RealMachinesDatabase = getDatabase(),
    private readonly loadAssets: DemoAssetLoader = loadBundledDemoAssets,
  ) {
    this.clients = new ClientRepository(database)
    this.machines = new MachineRepository(database)
    this.technicians = new TechnicianRepository(database)
    this.inspections = new InspectionRepository(database)
    this.photos = new PhotoRepository(database)
  }

  async exists(): Promise<boolean> {
    return executeDatabaseOperation('verificar dados de demonstração', async () =>
      Boolean(
        await this.database.auditLogs
          .where('[entityType+entityId]')
          .equals(['demoData', DEMO_BATCH_ID])
          .and((log) => log.action === 'create')
          .first(),
      ),
    )
  }

  async seed(): Promise<DemoDataResult> {
    if (await this.exists()) {
      throw new DatabaseOperationError('conflict', 'Os dados de demonstração já foram criados.')
    }
    const assets = await this.loadAssets()
    assertAsset(assets.hydraulic, 'hidráulica')
    assertAsset(assets.track, 'esteira')

    return executeDatabaseOperation('criar dados de demonstração', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.technicians,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.photos,
          this.database.photoPreviews,
          this.database.signatures,
          this.database.counters,
          this.database.auditLogs,
        ],
        async () => {
          if (await this.exists()) {
            throw new DatabaseOperationError(
              'conflict',
              'Os dados de demonstração já foram criados.',
            )
          }

          const client = await this.clients.create({
            companyName: 'DEMONSTRAÇÃO — Fazenda Horizonte',
            farmOrSite: 'Unidade Fictícia Norte',
            contactName: 'Responsável fictício',
            phone: '(63) 90000-0000',
            email: 'demo@example.invalid',
            address: 'Endereço fictício — não utilizar para atendimento',
            notes: 'DADO DE DEMONSTRAÇÃO. Não representa cliente real.',
          })
          const machine = await this.machines.create({
            clientId: client.id,
            equipment: 'Escavadeira de demonstração',
            brand: 'Marca fictícia',
            model: 'RM-Demo',
            year: 2024,
            serialChassis: 'DEMO-NAO-REAL-0001',
            currentHourmeter: 1_250,
            notes: 'DADO DE DEMONSTRAÇÃO. Máquina e série totalmente fictícias.',
          })
          const technician = await this.technicians.create({
            name: 'DEMONSTRAÇÃO — Técnico fictício',
            role: 'Técnico de homologação',
            phone: '(63) 90000-0001',
          })
          const draft = await this.inspections.createDraft({
            clientId: client.id,
            machineId: machine.id,
            technicianId: technician.id,
            inspectionDate: currentCivilDate(),
            hourmeter: 1_275,
          })
          const now = new Date().toISOString()
          const results = await this.database.inspectionResults
            .where('inspectionId')
            .equals(draft.id)
            .toArray()
          const demoResults = results.map((result) => {
            const status =
              result.checklistItemId === '7.2'
                ? ('critical' as const)
                : result.checklistItemId === '2.1'
                  ? ('attention' as const)
                  : ('good' as const)
            const observation =
              result.checklistItemId === '7.2'
                ? 'Exemplo fictício: desgaste crítico usado somente para demonstrar a prioridade.'
                : result.checklistItemId === '2.1'
                  ? 'Exemplo fictício: programar conferência dos acoplamentos em até 15 dias.'
                  : ''
            return { ...result, status, observation, updatedAt: now }
          })
          await this.database.inspectionResults.bulkPut(demoResults)
          const summary = summarizeInspectionResults(demoResults)
          await this.inspections.update(draft.id, {
            diagnosis:
              'DIAGNÓSTICO DE DEMONSTRAÇÃO.\n' +
              'Cenário inteiramente fictício para validar checklist, prioridade, fotos, PDF e histórico.',
            authorization: 'no',
            responsibleName: 'Responsável fictício de demonstração',
            clientSignatureWaiverReason:
              'Assinatura omitida porque este é um relatório fictício de demonstração.',
            clientSignatureWaiverConfirmedAt: now,
            currentStep: 7,
          })

          const hydraulicResult = demoResults.find((result) => result.checklistItemId === '2.1')
          if (!hydraulicResult) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Item hidráulico de demonstração não encontrado.',
            )
          }
          const hydraulicPhoto = await this.photos.create(
            {
              inspectionId: draft.id,
              resultId: hydraulicResult.id,
              category: 'item',
              blob: assets.hydraulic.full,
              mimeType: 'image/jpeg',
              width: assets.hydraulic.width,
              height: assets.hydraulic.height,
              size: assets.hydraulic.full.size,
              caption: 'Imagem genérica autorizada — mangueiras hidráulicas de máquina fictícia.',
              order: 0,
            },
            {
              blob: assets.hydraulic.thumbnail,
              mimeType: 'image/jpeg',
              width: assets.hydraulic.thumbnailWidth,
              height: assets.hydraulic.thumbnailHeight,
              size: assets.hydraulic.thumbnail.size,
            },
          )
          const trackPhoto = await this.photos.create(
            {
              inspectionId: draft.id,
              category: 'general',
              blob: assets.track.full,
              mimeType: 'image/jpeg',
              width: assets.track.width,
              height: assets.track.height,
              size: assets.track.full.size,
              caption: 'Imagem genérica autorizada — esteira de máquina fictícia.',
              order: 1,
            },
            {
              blob: assets.track.thumbnail,
              mimeType: 'image/jpeg',
              width: assets.track.thumbnailWidth,
              height: assets.track.thumbnailHeight,
              size: assets.track.thumbnail.size,
            },
          )
          const inspection = await this.inspections.complete(draft.id)
          await writeAuditLog(
            this.database,
            'demoData',
            DEMO_BATCH_ID,
            'create',
            {
              clientId: client.id,
              machineId: machine.id,
              technicianId: technician.id,
              inspectionId: inspection.id,
              reportNumber: inspection.reportNumber,
              photoIds: [hydraulicPhoto.id, trackPhoto.id],
              totals: {
                good: summary.good,
                attention: summary.attention,
                critical: summary.critical,
              },
            },
            new Date().toISOString(),
          )
          invalidateBackupStatus(this.database)
          return {
            client,
            machine,
            technician,
            inspection,
            photos: [hydraulicPhoto, trackPhoto],
          }
        },
      ),
    )
  }

  async clear(): Promise<boolean> {
    return executeDatabaseOperation('limpar dados de demonstração', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.technicians,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.photos,
          this.database.photoPreviews,
          this.database.signatures,
          this.database.auditLogs,
        ],
        async () => {
          const marker = await this.database.auditLogs
            .where('[entityType+entityId]')
            .equals(['demoData', DEMO_BATCH_ID])
            .and((log) => log.action === 'create')
            .first()
          if (!marker) return false

          const references = readReferences(marker.changes)
          const [clientMachines, clientInspections, machineInspections, technicianInspections] =
            await Promise.all([
              this.database.machines.where('clientId').equals(references.clientId).toArray(),
              this.database.inspections.where('clientId').equals(references.clientId).toArray(),
              this.database.inspections.where('machineId').equals(references.machineId).toArray(),
              this.database.inspections
                .where('technicianId')
                .equals(references.technicianId)
                .toArray(),
            ])
          const linkedInspectionIds = new Set(
            [...clientInspections, ...machineInspections, ...technicianInspections].map(
              (inspection) => inspection.id,
            ),
          )
          const hasExternalLinks =
            clientMachines.some((machine) => machine.id !== references.machineId) ||
            [...linkedInspectionIds].some((id) => id !== references.inspectionId) ||
            (await this.database.photos.where('machineId').equals(references.machineId).count()) >
              0 ||
            (await this.database.signatures
              .where('technicianId')
              .equals(references.technicianId)
              .count()) > 0
          if (hasExternalLinks) {
            throw new DatabaseOperationError(
              'protected_record',
              'Os cadastros de demonstração passaram a ter vínculos externos.',
              'A demonstração não pode ser removida porque seus cadastros foram usados em outros registros.',
            )
          }

          const inspectionId = references.inspectionId
          const [results, photos, previews, signatures] = inspectionId
            ? await Promise.all([
                this.database.inspectionResults
                  .where('inspectionId')
                  .equals(inspectionId)
                  .toArray(),
                this.database.photos.where('inspectionId').equals(inspectionId).toArray(),
                this.database.photoPreviews.where('inspectionId').equals(inspectionId).toArray(),
                this.database.signatures.where('inspectionId').equals(inspectionId).toArray(),
              ])
            : [[], [], [], []]

          await this.database.photoPreviews.bulkDelete(previews.map((preview) => preview.id))
          await this.database.photos.bulkDelete(photos.map((photo) => photo.id))
          await this.database.signatures.bulkDelete(signatures.map((signature) => signature.id))
          await this.database.inspectionResults.bulkDelete(results.map((result) => result.id))
          if (inspectionId) await this.database.inspections.delete(inspectionId)
          await this.database.machines.delete(references.machineId)
          await this.database.clients.delete(references.clientId)
          await this.database.technicians.delete(references.technicianId)

          const entityIds = [
            references.clientId,
            references.machineId,
            references.technicianId,
            ...(inspectionId ? [inspectionId] : []),
            ...results.map((result) => result.id),
            ...photos.map((photo) => photo.id),
            ...signatures.map((signature) => signature.id),
            ...references.photoIds,
            DEMO_BATCH_ID,
          ]
          await this.database.auditLogs
            .where('entityId')
            .anyOf([...new Set(entityIds)])
            .delete()
          await writeAuditLog(
            this.database,
            'demoData',
            `${DEMO_BATCH_ID}-cleaned`,
            'delete',
            {},
            new Date().toISOString(),
          )
          invalidateBackupStatus(this.database)
          return true
        },
      ),
    )
  }
}
